/**
 * ContactSelector v2 — универсальный селектор контактов с фильтрами
 *
 * Фильтр по типу: all | partner | child | parent | friend | colleague | pet
 * API: GET /api/synastry/contacts/extended?type=...
 * Показывает: имя, тип, дата рождения
 * Клик → onSelect(contact)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { handleApiError } from '../utils/apiErrors';
import { logger } from '../utils/logger';

// ============================================================
// Types
// ============================================================

export type ContactType = 'partner' | 'child' | 'parent' | 'friend' | 'colleague' | 'pet';

export interface ExtendedContact {
  id: string;
  name: string;
  type: ContactType;
  birthDate: string;
  birthTime?: string;
  birthPlace?: string;
  notes?: string;
  avatar?: string;
}

interface ContactSelectorProps {
  onSelect: (contact: ExtendedContact) => void;
  selectedId?: string;
  typeFilter?: ContactType | 'all';
  placeholder?: string;
  maxVisible?: number;
}

// ============================================================
// Constants
// ============================================================

const TYPE_OPTIONS: { value: ContactType | 'all'; label: string; icon: string }[] = [
  { value: 'all', label: 'Все', icon: '👥' },
  { value: 'partner', label: 'Партнёр', icon: '💑' },
  { value: 'child', label: 'Ребёнок', icon: '👶' },
  { value: 'parent', label: 'Родитель', icon: '👨‍👩‍👧' },
  { value: 'friend', label: 'Друг', icon: '🤝' },
  { value: 'colleague', label: 'Коллега', icon: '💼' },
  { value: 'pet', label: 'Питомец', icon: '🐾' },
];

const TYPE_COLORS: Record<ContactType, string> = {
  partner: '#E91E63',
  child: '#4CAF50',
  parent: '#2196F3',
  friend: '#FF9800',
  colleague: '#9C27B0',
  pet: '#795548',
};

// ============================================================
// Styles — Astranum design standard
// ============================================================

const colors = {
  bg: '#0a0814',
  bgCard: 'rgba(13, 20, 40, 0.85)',
  border: 'rgba(212, 175, 55, 0.12)',
  gold: '#d4af37',
  goldLight: '#f4e4a6',
  text: '#f0f0f0',
  textMuted: '#b0b0b0',
};

const radius = { sm: '8px', md: '12px', lg: '16px', xxl: '24px' };
const spacing = { xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '24px' };
const fontSize = { sm: '13px', md: '14px', lg: '16px' };

const containerStyle = (isMobile: boolean): React.CSSProperties => ({
  background: colors.bgCard,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.xxl,
  padding: isMobile ? '12px' : spacing.lg,
  boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
});

const filterBarStyle: React.CSSProperties = {
  display: 'flex',
  gap: spacing.xs,
  marginBottom: spacing.md,
  flexWrap: 'wrap',
};

const filterBtnStyle = (active: boolean, isMobile: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: spacing.xs,
  padding: isMobile ? '4px 8px' : '6px 12px',
  background: active ? 'rgba(212, 175, 55, 0.15)' : 'rgba(255, 255, 255, 0.03)',
  border: `1px solid ${active ? colors.gold : colors.border}`,
  borderRadius: radius.sm,
  color: active ? colors.gold : colors.textMuted,
  fontSize: isMobile ? '11px' : fontSize.sm,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  fontFamily: 'var(--font-sans)',
});

const contactItemStyle = (selected: boolean, isMobile: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: isMobile ? '8px' : spacing.md,
  padding: isMobile ? '10px 8px' : spacing.md,
  background: selected ? 'rgba(212, 175, 55, 0.1)' : 'rgba(255, 255, 255, 0.02)',
  border: `1px solid ${selected ? colors.gold : 'rgba(255, 255, 255, 0.05)'}`,
  borderRadius: radius.md,
  marginBottom: spacing.sm,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
});

const typeBadgeStyle = (color: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '3px',
  padding: '2px 8px',
  background: `${color}20`,
  border: `1px solid ${color}40`,
  borderRadius: '6px',
  color,
  fontSize: '11px',
  fontWeight: 'bold',
  fontFamily: 'var(--font-sans)',
});

// ============================================================
// Component
// ============================================================

const ContactSelector: React.FC<ContactSelectorProps> = ({
  onSelect,
  selectedId,
  typeFilter: initialTypeFilter = 'all',
  placeholder = 'Выберите контакт...',
  maxVisible = 20,
}) => {
  const [contacts, setContacts] = useState<ExtendedContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<ContactType | 'all'>(initialTypeFilter);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Load contacts
  useEffect(() => {
    loadContacts();
  }, [typeFilter]);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = typeFilter !== 'all' ? `?type=${typeFilter}` : '';
      const res = await api.get<ExtendedContact[]>(`/synastry/contacts/extended${query}`);
      if (res.success && Array.isArray(res.data)) {
        setContacts(res.data.slice(0, maxVisible));
      } else {
        setContacts([]);
      }
    } catch (err) {
      setError(handleApiError(err, 'Не удалось загрузить контакты'));
      logger.error('ContactSelector loadContacts error', err);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, maxVisible]);

  // ============================================================
  // Render
  // ============================================================

  return (
    <div style={containerStyle(isMobile)}>
      {/* Filter bar */}
      <div style={filterBarStyle}>
        {TYPE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setTypeFilter(opt.value)}
            style={filterBtnStyle(typeFilter === opt.value, isMobile)}
            onMouseEnter={(e) => {
              if (typeFilter !== opt.value) {
                (e.currentTarget as HTMLButtonElement).style.borderColor = `${colors.gold}60`;
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255, 255, 255, 0.06)';
              }
            }}
            onMouseLeave={(e) => {
              if (typeFilter !== opt.value) {
                (e.currentTarget as HTMLButtonElement).style.borderColor = colors.border;
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255, 255, 255, 0.03)';
              }
            }}
          >
            <span>{opt.icon}</span>
            <span>{opt.label}</span>
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: spacing.xl, color: colors.textMuted, fontSize: isMobile ? '13px' : '14px' }}>
          Загрузка контактов...
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div style={{ textAlign: 'center', padding: spacing.md, color: colors.error, fontSize: isMobile ? '13px' : '14px' }}>
          {error}
          <button
            onClick={loadContacts}
            style={{
              ...filterBtnStyle(false, isMobile),
              marginLeft: spacing.sm,
              color: colors.gold,
            }}
          >
            Повторить
          </button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && contacts.length === 0 && (
        <div style={{ textAlign: 'center', padding: spacing.xl, color: colors.textMuted }}>
          <div style={{ fontSize: isMobile ? '28px' : '32px', marginBottom: spacing.sm }}>
            {TYPE_OPTIONS.find((o) => o.value === typeFilter)?.icon || '👥'}
          </div>
          <p style={{ margin: 0, fontSize: isMobile ? '13px' : fontSize.md }}>{placeholder}</p>
        </div>
      )}

      {/* Contact list */}
      {!loading && !error && contacts.length > 0 && (
        <div>
          {contacts.map((contact) => {
            const isSelected = contact.id === selectedId;
            const typeColor = TYPE_COLORS[contact.type] || colors.textMuted;
            const typeLabel = TYPE_OPTIONS.find((o) => o.value === contact.type)?.label || contact.type;

            return (
              <div
                key={contact.id}
                onClick={() => onSelect(contact)}
                style={contactItemStyle(isSelected, isMobile)}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    (e.currentTarget as HTMLDivElement).style.borderColor = `${colors.gold}50`;
                    (e.currentTarget as HTMLDivElement).style.background = 'rgba(255, 255, 255, 0.04)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(255, 255, 255, 0.05)';
                    (e.currentTarget as HTMLDivElement).style.background = 'rgba(255, 255, 255, 0.02)';
                  }
                }}
              >
                {/* Avatar / icon */}
                <div
                  style={{
                    width: isMobile ? '34px' : '40px',
                    height: isMobile ? '34px' : '40px',
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${typeColor}40 0%, ${typeColor}20 100%)`,
                    border: `1px solid ${typeColor}50`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isMobile ? '15px' : '18px',
                    flexShrink: 0,
                  }}
                >
                  {contact.avatar || TYPE_OPTIONS.find((o) => o.value === contact.type)?.icon || '👤'}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    color: isSelected ? colors.gold : colors.text,
                    fontSize: isMobile ? '13px' : fontSize.md,
                    fontWeight: isSelected ? 'bold' : 'normal',
                    marginBottom: '2px',
                    fontFamily: 'var(--font-sans)',
                  }}>
                    {contact.name}
                  </div>
                  <div style={{
                    color: colors.textMuted,
                    fontSize: isMobile ? '11px' : fontSize.sm,
                    fontFamily: 'var(--font-sans)',
                  }}>
                    {contact.birthDate ? new Date(contact.birthDate).toLocaleDateString('ru-RU') : 'Дата не указана'}
                    {contact.birthPlace && ` • ${contact.birthPlace}`}
                  </div>
                </div>

                {/* Type badge */}
                <div style={typeBadgeStyle(typeColor)}>
                  {typeLabel}
                </div>

                {/* Selected indicator */}
                {isSelected && (
                  <span style={{ color: colors.gold, fontSize: '16px' }}>✓</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContactSelector;
